import type { ReactNode } from 'react';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { useQuery as useConvexQuery } from 'convex/react';
import { api } from '../../../../convex/_generated/api';

export const Route = createFileRoute('/admin/import-metrics')({
  validateSearch: (search: Record<string, unknown>) => ({
    days: Number(search.days ?? 30),
    topN: Number(search.topN ?? 10),
  }),
  beforeLoad: async ({ context }) => {
    if (!context.isAdminHost) {
      throw redirect({ to: '/app' });
    }

    if (!context.userId) {
      throw redirect({ to: '/login' });
    }

    if (!context.isAdminAllowed) {
      throw redirect({ to: '/admin/forbidden' });
    }
  },
  component: AdminImportMetricsPage,
});

function AdminImportMetricsPage() {
  const navigate = useNavigate({ from: '/admin/import-metrics' });
  const search = Route.useSearch();

  const days = Number.isFinite(search.days) ? Math.max(1, Math.min(search.days, 365)) : 30;
  const topN = Number.isFinite(search.topN) ? Math.max(3, Math.min(search.topN, 50)) : 10;

  const metrics = useConvexQuery(api.recipeImportMetrics.adminDashboard, {
    days,
    topN,
    recentLimit: 40,
  });

  return (
    <div className="max-w-6xl mx-auto px-4 py-6 space-y-6">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-semibold text-ink">Recipe Import Metrics</h1>
          <p className="text-sm text-stone">Internal dashboard for import reliability.</p>
        </div>

        <form
          className="flex items-end gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            const formData = new FormData(event.currentTarget);
            const nextDays = Number(formData.get('days') ?? 30);
            const nextTopN = Number(formData.get('topN') ?? 10);
            navigate({
              search: {
                days: Number.isFinite(nextDays) ? Math.max(1, Math.min(nextDays, 365)) : 30,
                topN: Number.isFinite(nextTopN) ? Math.max(3, Math.min(nextTopN, 50)) : 10,
              },
            });
          }}
        >
          <label className="text-sm text-stone">
            Days
            <input name="days" type="number" min={1} max={365} defaultValue={days} className="ml-2 px-2 py-1 border border-mist rounded" />
          </label>
          <label className="text-sm text-stone">
            Top N
            <input name="topN" type="number" min={3} max={50} defaultValue={topN} className="ml-2 px-2 py-1 border border-mist rounded" />
          </label>
          <button type="submit" className="btn btn-primary">Apply</button>
        </form>
      </div>

      {!metrics ? (
        <div className="bg-white border border-mist rounded-xl p-6 text-stone">Loading metrics…</div>
      ) : (
        <>
          <section className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <MetricCard label="Attempts" value={metrics.totals.attempts.toLocaleString()} />
            <MetricCard label="Success" value={metrics.totals.successes.toLocaleString()} />
            <MetricCard label="Failures" value={metrics.totals.failures.toLocaleString()} />
            <MetricCard label="Success Rate" value={`${(metrics.totals.successRate * 100).toFixed(1)}%`} />
          </section>

          <section className="grid md:grid-cols-3 gap-4">
            <Panel title="Top Failing Domains">
              <ul className="space-y-2 text-sm">
                {metrics.topFailingDomains.length === 0 && <li className="text-stone">No failures in this window 🎉</li>}
                {metrics.topFailingDomains.map((item) => (
                  <li key={item.domain} className="flex items-start justify-between gap-2">
                    <div>
                      <p className="font-medium text-ink">{item.domain}</p>
                      <p className="text-stone text-xs">Top reason: {item.topReason}</p>
                    </div>
                    <p className="text-stone text-xs">{item.failures}/{item.attempts}</p>
                  </li>
                ))}
              </ul>
            </Panel>

            <Panel title="Reason Categories">
              <ul className="space-y-2 text-sm">
                {metrics.reasonCategoryBreakdown.map((item) => (
                  <li key={item.reasonCategory} className="flex justify-between gap-2">
                    <span className="text-ink">{item.reasonCategory}</span>
                    <span className="text-stone">{item.count}</span>
                  </li>
                ))}
              </ul>
            </Panel>

            <Panel title="Source Breakdown">
              <ul className="space-y-2 text-sm">
                {metrics.sourceBreakdown.map((item) => (
                  <li key={item.source} className="flex justify-between gap-2">
                    <span className="text-ink uppercase">{item.source}</span>
                    <span className="text-stone">{item.count}</span>
                  </li>
                ))}
              </ul>
            </Panel>
          </section>

          <Panel title="Recent Attempts">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-stone border-b border-mist">
                    <th className="py-2 pr-3">Time</th>
                    <th className="py-2 pr-3">Domain</th>
                    <th className="py-2 pr-3">Source</th>
                    <th className="py-2 pr-3">Result</th>
                    <th className="py-2 pr-3">Reason</th>
                    <th className="py-2 pr-3">Confidence</th>
                  </tr>
                </thead>
                <tbody>
                  {metrics.recentAttempts.map((attempt, index) => (
                    <tr key={`${attempt.domain}-${attempt.timestamp}-${index}`} className="border-b border-mist/60">
                      <td className="py-2 pr-3 text-stone">{new Date(attempt.timestamp).toLocaleString()}</td>
                      <td className="py-2 pr-3 text-ink">{attempt.domain}</td>
                      <td className="py-2 pr-3 uppercase text-stone">{attempt.source}</td>
                      <td className="py-2 pr-3">
                        <span className={attempt.success ? 'text-emerald-600' : 'text-rose-600'}>
                          {attempt.success ? 'success' : 'failed'}
                        </span>
                      </td>
                      <td className="py-2 pr-3 text-stone">{attempt.reasonCategory}</td>
                      <td className="py-2 pr-3 text-stone">
                        {typeof attempt.confidence === 'number' ? `${Math.round(attempt.confidence * 100)}%` : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Panel>
        </>
      )}
    </div>
  );
}

function MetricCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-white border border-mist rounded-xl p-4">
      <p className="text-xs uppercase tracking-wide text-stone">{label}</p>
      <p className="mt-1 text-2xl font-semibold text-ink">{value}</p>
    </div>
  );
}

function Panel({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="bg-white border border-mist rounded-xl p-4">
      <h2 className="font-semibold text-ink mb-3">{title}</h2>
      {children}
    </section>
  );
}
